import React, { useEffect, useRef, useState } from "react";
import { AppButton } from "../ui/AppButton";
import { ChevronDown } from "lucide-react";
import { exportCSV, exportExcel, exportToPDF } from "../../util/table-export";

interface ExportButtonProps {
  columns: any[];
  data: any[];
  fileName?: string;
  title?: string;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
  // custom icon support
  customIcon?: React.ReactNode;
  className?: string;
  menuClassName?: string;
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  columns,
  data,
  fileName,
  title = "Report",
  pagination,
  customIcon, // custom icon prop
  className = "", // custom className prop
  menuClassName = "w-36", // custom className prop for menu items
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Generate timestamp for consistent filenames across all formats
  const generateTimestamp = () => {
    return new Date()
      .toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
      .replace(/ /g, "_");
  };

  const handlePDFExport = () => {
    const timestamp = generateTimestamp();
    const baseFileName = fileName || "export";

    exportToPDF(columns, data, {
      fileName: `${baseFileName}_${timestamp}.pdf`,
      title: title,
      showPageNumbers: true,
      pagination: pagination,
    });
    setOpen(false);
  };

  const handleCSVExport = () => {
    const timestamp = generateTimestamp();
    const baseFileName = fileName || "export";

    exportCSV(columns, data, `${baseFileName}_${timestamp}.csv`);
    setOpen(false);
  };

  const handleExcelExport = () => {
    const timestamp = generateTimestamp();
    const baseFileName = fileName || "export";

    exportExcel(columns, data, `${baseFileName}_${timestamp}.xlsx`);
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <AppButton
        customIcon={customIcon as string} // custom icon
        onClick={() => setOpen((prev) => !prev)}
        className={`${className} bg-[#DDDDDD] `} // custom className
      >
        <span className="text-black text-opacity-50">Export</span>
        <ChevronDown className="ml-1 text-black text-opacity-50" />
      </AppButton>

      {open && (
        <div
          className={`absolute right-0 mt-2 bg-white border shadow rounded-xl z-20 ${menuClassName}`}
        >
          <button
            className="w-full text-left px-4 py-2 text-xs hover:bg-gray-100"
            onClick={handlePDFExport}
          >
            PDF <span className="text-xs text-gray-500">(.pdf format)</span>
          </button>
          <button
            className="w-full text-left px-4 py-2 text-xs hover:bg-gray-100"
            onClick={handleCSVExport}
          >
            CSV <span className="text-xs text-gray-500">(.csv format)</span>
          </button>
          <button
            className="w-full text-left px-4 py-2 text-xs hover:bg-gray-100"
            onClick={handleExcelExport}
          >
            Excel <span className="text-xs text-gray-500">(.xlsx format)</span>
          </button>
        </div>
      )}
    </div>
  );
};
